import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { fmtTL } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, ArrowRightLeft, Landmark, Coins } from "lucide-react";

export function AccountsPage({ kind }) {
  const { isAdmin } = useAuth();
  const [accounts, setAccounts] = useState({ banks: [], cash: [] });
  const [open, setOpen] = useState(false);
  const [xopen, setXopen] = useState(false);
  const [form, setForm] = useState({ name: "", kind, bank_name: "", iban: "", account_no: "", opening_balance: 0, note: "" });
  const [x, setX] = useState({ from_id: "", from_type: kind, to_id: "", to_type: kind, amount: 0, note: "" });
  const load = async () => setAccounts((await api.get("/accounts")).data);
  useEffect(() => { load(); setForm((f) => ({ ...f, kind })); setX((s) => ({ ...s, from_type: kind, to_type: kind })); }, [kind]);

  const list = kind === "bank" ? accounts.banks : accounts.cash;
  const total = list.reduce((s, a) => s + (a.balance || 0), 0);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name) return toast.error("Ad gerekli");
    await api.post("/accounts", { ...form, opening_balance: Number(form.opening_balance) });
    toast.success("Eklendi"); setOpen(false); load();
    setForm({ name: "", kind, bank_name: "", iban: "", account_no: "", opening_balance: 0, note: "" });
  };
  const del = async (id) => { if (!window.confirm("Silinsin mi?")) return; await api.delete(`/accounts/${kind}/${id}`); load(); };
  const doTransfer = async (e) => {
    e.preventDefault();
    if (!x.from_id || !x.to_id || x.from_id === x.to_id) return toast.error("Kaynak ve hedef farklı olmalı");
    if (!Number(x.amount) || Number(x.amount) <= 0) return toast.error("Tutar girin");
    await api.post("/transfers", { ...x, amount: Number(x.amount) });
    toast.success("Transfer yapıldı"); setXopen(false); load();
  };
  const allAccounts = [...accounts.banks.map((b) => ({ ...b, type: "bank" })), ...accounts.cash.map((c) => ({ ...c, type: "cash" }))];

  const Icon = kind === "bank" ? Landmark : Coins;
  const title = kind === "bank" ? "Banka Hesapları" : "Kasalar";

  return (
    <div className="space-y-6" data-testid={`${kind}-page`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight">{title}</h1>
          <p className="text-sm text-muted-foreground">Toplam: <span className="num font-semibold text-primary">{fmtTL(total)}</span></p>
        </div>
        <div className="flex gap-2">
          <Dialog open={xopen} onOpenChange={setXopen}>
            <DialogTrigger asChild><Button variant="outline"><ArrowRightLeft size={16} className="mr-1" /> Transfer</Button></DialogTrigger>
            <DialogContent className="bg-popover">
              <DialogHeader><DialogTitle>Hesaplar Arası Transfer</DialogTitle></DialogHeader>
              <form onSubmit={doTransfer} className="space-y-3">
                <div><Label>Kaynak</Label>
                  <Select value={x.from_id} onValueChange={(v) => { const a = allAccounts.find(z => z.id === v); setX({ ...x, from_id: v, from_type: a.type }); }}>
                    <SelectTrigger><SelectValue placeholder="Seç" /></SelectTrigger>
                    <SelectContent className="bg-popover">{allAccounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} · {fmtTL(a.balance)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Hedef</Label>
                  <Select value={x.to_id} onValueChange={(v) => { const a = allAccounts.find(z => z.id === v); setX({ ...x, to_id: v, to_type: a.type }); }}>
                    <SelectTrigger><SelectValue placeholder="Seç" /></SelectTrigger>
                    <SelectContent className="bg-popover">{allAccounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Tutar</Label><Input type="number" step="0.01" value={x.amount} onChange={(e) => setX({ ...x, amount: e.target.value })} /></div>
                <Button type="submit" className="w-full">Transfer Yap</Button>
              </form>
            </DialogContent>
          </Dialog>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button><Plus size={16} className="mr-1" /> Yeni {kind === "bank" ? "Banka" : "Kasa"}</Button></DialogTrigger>
            <DialogContent className="bg-popover">
              <DialogHeader><DialogTitle>Yeni {kind === "bank" ? "Banka Hesabı" : "Kasa"}</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-3">
                <div><Label>Ad *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                {kind === "bank" && (<>
                  <div><Label>Banka Adı</Label><Input value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} /></div>
                  <div><Label>IBAN</Label><Input value={form.iban} onChange={(e) => setForm({ ...form, iban: e.target.value })} /></div>
                </>)}
                <div><Label>Açılış Bakiyesi</Label><Input type="number" step="0.01" value={form.opening_balance} onChange={(e) => setForm({ ...form, opening_balance: e.target.value })} /></div>
                <Button type="submit" className="w-full">Kaydet</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {list.map((a) => (
          <Card key={a.id} className="p-5 hover:border-primary/50 transition-colors">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary"><Icon size={18} /></div>
                <div>
                  <div className="font-bold">{a.name}</div>
                  {a.bank_name && <div className="text-xs text-muted-foreground">{a.bank_name}</div>}
                </div>
              </div>
              {isAdmin && <Button variant="ghost" size="icon" onClick={() => del(a.id)} data-testid={`account-delete-${a.id}`}><Trash2 size={14} /></Button>}
            </div>
            <div className="text-xs text-muted-foreground uppercase mb-1">Bakiye</div>
            <div className="text-2xl font-bold num text-primary">{fmtTL(a.balance)}</div>
            {a.iban && <div className="text-xs text-muted-foreground mt-2 font-mono">{a.iban}</div>}
          </Card>
        ))}
        {list.length === 0 && <div className="col-span-full text-center py-8 text-muted-foreground">Henüz {kind === "bank" ? "banka" : "kasa"} yok</div>}
      </div>
    </div>
  );
}
